import { ComponentProps, forwardRef } from "react";

import { Currency } from "currency";
import { useField } from "formik";
import { TextInput as RNTextInput } from "react-native";
import { TextInput } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

const NON_NUMBERS_REGEX = /[^0-9.]/g;
const REPEATED_DECIMAL_POINTS_REGEX = /\.{2,}/g;
const MAX_DECIMAL_PLACES_REGEX = /(\.\d{0,2}).*/g;

const CURRENCY_MAP = {
  usd: "$",
  crc: "₡",
};

type Props = ComponentProps<typeof TextInput> & { currency: Currency };
const CurrencyInput = forwardRef<RNTextInput, Props>(function CurrencyInput(
  { currency, value, onChangeText, ...props },
  ref
) {
  const filteredOnChangeText = (text: string) => {
    // Remove thousands separators (commas) in groups of three digits
    const withoutThousandsSeparator = text.replace(
      /(\d),(?=(\d{3})+(?!\d))/g,
      "$1"
    );

    const withDecimalSeparator = withoutThousandsSeparator.replace(",", ".");

    const sanitized = withDecimalSeparator
      .replace(NON_NUMBERS_REGEX, "") // replace non-digits
      .replace(REPEATED_DECIMAL_POINTS_REGEX, ".") // ensure no duplicate periods
      .replace(MAX_DECIMAL_PLACES_REGEX, "$1"); // limit digits after the period

    onChangeText?.(sanitized);
  };

  const getKeyboardType = (currency: Currency) => {
    switch (currency) {
      case "usd":
        return "decimal-pad";
      case "crc":
        return "number-pad";
    }
  };

  const tailwind = useTailwind();

  return (
    <TextInput
      mode="outlined"
      ref={ref}
      left={
        <TextInput.Affix
          text={CURRENCY_MAP[currency]}
          textStyle={tailwind("-mr-2 mt-0.5")}
        />
      }
      value={value}
      onChangeText={filteredOnChangeText}
      keyboardType={getKeyboardType(currency)}
      returnKeyType="done"
      returnKeyLabel="next" // only works on android https://github.com/facebook/react-native/issues/31794
      {...props}
    />
  );
});

export default CurrencyInput;

type FormikProps = Props & { fieldName: string };
export function FormikCurrencyInput({
  currency,
  fieldName,
  ...props
}: FormikProps) {
  const [field, , helpers] = useField<string>(fieldName);

  return (
    <CurrencyInput
      currency={currency}
      value={field.value}
      onChangeText={helpers.setValue}
      onBlur={field.onBlur(fieldName)}
      {...props}
    />
  );
}
